import { gql, useQuery } from "@apollo/client";
import {
  SalesFilter,
  Shimmers,
  StatsPageLoader,
  TableBodyComponent,
} from "components";
import lodash from "lodash";
import moment from "moment";
import numeral from "numeral";
import { FC, useMemo } from "react";
import { useSearch } from "react-location";
import { LocationGenerics } from "router/location";
import { getPeriod, useUpdatedSummaryData } from "utils";
const DATE_FORMAT = "YYYY-MM-DD";

const SUMMARY = gql`
  query GetSalesSummary(
    $region: ID
    $district: ID
    $tariffClass: ID
    $serviceClass: ServiceClass
    $month: PositiveInt
    $year: PositiveInt
    $type: SalesSummaryType
  ) {
    rows: getSalesSummary(
      region: $region
      district: $district
      tariffClass: $tariffClass
      serviceClass: $serviceClass
      month: $month
      year: $year
      type: $type
    ) {
      _id
      identifier
      billConsumptionValue
      outstandingAmount
      lifeLineAmount
      firstThresholdAmount
      secondThresholdAmount
      energyCharge
      serviceCharge
      energyPlusServiceCharge
      powerFactorSurcharge
      vatCharge
      nhilCharge
      getfundCharge
      streetLightCharge
      nationalElectrificationCharge
      billAmount
      paymentsAmount
      adjustmentAmount
      amountDue
      debtAmount
    }
  }
`;

const Headers = [
  { label: "Consumption (KWH)", key: "billConsumptionValue", format: "0,0" },
  { label: "Total Amount Payable (GHS)", key: "amountDue", format: "0,0.00" },
  {
    label: "Bill Payment Amount (GHS)",
    key: "paymentsAmount",
    format: "0,0.00",
  },
  { label: "Total Debt (GHS)", key: "debtAmount", format: "0,0.00" },
];

export const SalesDebtsSummaryPage: FC = () => {
  const searchParams = useSearch<LocationGenerics>();

  const filter = useMemo(
    () => ({
      region: searchParams.region || undefined,
      district: searchParams.district || undefined,
      month:
        moment(searchParams.fromDate || new Date(), DATE_FORMAT).month() + 1,
      year: moment(searchParams.fromDate || new Date(), DATE_FORMAT).year(),
      type: "ByDistrict",
      ...searchParams?.filter,
    }),
    [searchParams],
  );

  const { data, loading, networkStatus } = useQuery(SUMMARY, {
    variables: filter,
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "network-only",
  });

  const { records } = useUpdatedSummaryData(data);

  return (
    <div className="flex-1 flex flex-col mx-auto min-w-0 min-h-full">
      <SalesFilter filters={["serviceClasses", "tariffClasses"]} />
      {loading ? (
        <StatsPageLoader />
      ) : (
        <div className="flex-1 min-h-full min-w-0 py-4 sm:py-6 px-6 overflow-hidden overflow-y-auto light flex flex-col">
          <div className="flex flex-col">
            <div className="overflow-y-auto">
              <TableBodyComponent
                title={`Sales Per ${
                  searchParams?.region ? `District` : "Region"
                }`}
                exportFileName={`Sales Per ${
                  searchParams?.region ? "District" : "Region"
                } For ${getPeriod(searchParams)}`}
                exportTypes={[
                  "EXCEL",
                  // "PDF"
                ]}
                noSpace={true}
                loading={loading && ![4, 6].includes(networkStatus)}
                data={{
                  ...records,
                  rows: [
                    ...records.rows.sort(
                      (a: { identifier: string }, b: { identifier: string }) =>
                        a.identifier.localeCompare(b.identifier),
                    ),
                    {
                      _id: "total",
                      identifier: "Total",
                      ...lodash.transform(
                        records.rows,
                        (acc: any, obj) => {
                          lodash.forOwn(
                            lodash.omit(obj, ["_id", "identifier"]),
                            (value, key) => {
                              acc[key] = (acc[key] || 0) + value;
                            },
                          );
                        },
                        {},
                      ),
                    },
                  ],
                }}
                renderColumns={() => (
                  <>
                    <tr className="hidden print:table-row">
                      <th
                        scope="col"
                        colSpan={1 + Headers.length}
                        className="sticky top-0 border-t border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-center text-md font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                      >
                        Customers Per{" "}
                        {searchParams?.region ? "District" : "Region"} For{" "}
                        {getPeriod(searchParams)}
                      </th>
                    </tr>
                    <tr>
                      {!!searchParams.region ? (
                        <th
                          scope="col"
                          className="sticky top-0 left-0 z-10 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                        >
                          District
                        </th>
                      ) : (
                        <th
                          scope="col"
                          className="sticky top-0 left-0 z-10 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                        >
                          Region
                        </th>
                      )}
                      {Headers.map((header) => (
                        <th
                          key={header.key}
                          scope="col"
                          className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-right text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                        >
                          {header.label}
                        </th>
                      ))}
                    </tr>
                  </>
                )}
                renderLoader={() => (
                  <tr className="border-t border-gray-200 print:border-gray-700">
                    <td className="px-4 py-4 border-b border-gray-200 print:border-gray-700">
                      <Shimmers.SingleShimmer />
                    </td>
                    <td className="px-4 py-4 border-b border-gray-200 print:border-gray-700">
                      <Shimmers.SingleShimmer />
                    </td>
                    {Headers.map((header) => (
                      <td
                        key={header.key}
                        className="px-4 py-4 border-b border-gray-200 print:border-gray-700"
                      >
                        <Shimmers.SingleShimmer />
                      </td>
                    ))}
                  </tr>
                )}
                renderItem={(item) => (
                  <tr
                    key={item._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                  >
                    <td className="whitespace-nowrap sticky left-0 z-10 bg-white px-4 py-4 text-normal text-gray-700 border-b border-gray-200 print:border-gray-700">
                      {item?.identifier || "N/A"}
                    </td>

                    {Headers.map((header) => (
                      <td className="whitespace-nowrap px-4 py-4 text-normal text-right text-gray-700 border-b border-gray-200 print:border-gray-700">
                        {numeral(lodash.get(item, header.key, 0)).format(
                          header.format,
                        ) || "N/A"}
                      </td>
                    ))}
                  </tr>
                )}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
